import { Link, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { candidatesPath } from "@/routes"
import type {
  LeaderboardEmptyKind,
  LeaderboardPeriod,
} from "@/types/leaderboard"

/**
 * OKS-51 — Empty-state surface. UX Spec §3.
 *
 * Two flavours driven by the server-emitted `empty_kind` discriminator:
 *
 *   - "no_crew" — brand has zero active crew members.
 *     • Creator viewers see neutral copy.
 *     • Brand admins see an "Invite creators" CTA pointing at the
 *       candidates surface via `candidatesPath()` (OKS-847 — this
 *       was a hardcoded `/candidates` literal, the last one in the
 *       frontend). It stays a real Inertia `<Link>`, NOT a
 *       `router.visit` click handler: UX Spec §6 requires an
 *       inspectable, focusable, middle-clickable href. Contrast
 *       "Switch to All time" below — a filter toggle rather than
 *       navigation, so a button is correct there. Same shape as
 *       the `CrewAnalyticsEmpty` twin.
 *
 *   - "no_xp_in_window" — brand has crew but no XP earned in the
 *     selected window. Both audiences get a one-tap "Switch to All
 *     time" link (omitted when already on All time).
 *
 * The component branches on `audience` to pick the role-tailored
 * copy — pages pass `"creator"` or `"admin"`. Same component for
 * both surfaces so empty-state evolution stays in one place.
 */
interface LeaderboardEmptyProps {
  emptyKind: NonNullable<LeaderboardEmptyKind>
  period: LeaderboardPeriod
  audience: "creator" | "admin"
  /** Route helper to navigate "Switch to All time" — page passes the appropriate one. */
  switchAllTimeUrl: string
}

export function LeaderboardEmpty({
  emptyKind,
  period,
  audience,
  switchAllTimeUrl,
}: LeaderboardEmptyProps) {
  const { t } = useTranslation()

  if (emptyKind === "no_crew") {
    return (
      <Card data-testid="leaderboard-empty-no-crew">
        <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-muted-foreground text-sm">
            {t(`leaderboard.empty.no_crew_${audience}`)}
          </p>
          {audience === "admin" && (
            <Button asChild>
              <Link
                href={candidatesPath()}
                data-testid="leaderboard-empty-invite-cta"
              >
                {t("leaderboard.empty.invite_creators_cta")}
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  // emptyKind === "no_xp_in_window"
  const showSwitchLink = period !== "all"
  return (
    <Card data-testid="leaderboard-empty-no-xp">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <p className="text-muted-foreground text-sm">
          {t(`leaderboard.empty.no_xp_${audience}`, {
            window: t(`leaderboard.window.${period}`),
          })}
        </p>
        {showSwitchLink && (
          <Button
            variant="link"
            size="sm"
            data-testid="leaderboard-empty-switch-all-time"
            onClick={() =>
              router.visit(switchAllTimeUrl, { preserveScroll: true })
            }
          >
            {t("leaderboard.empty.switch_all_time")}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
